import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { 
  ArrowRight, Calculator, Building2, User, Users, TrendingUp, 
  Target, BarChart3, Calendar, Award, CheckCircle, Sparkles, Package
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAudience, AUDIENCE_TYPES } from '../context/AudienceContext';
import { useABTest } from '../context/ABTestContext';
import FiveDAssessment from './FiveDAssessment';
import { apiUrl, openBookDemo } from '../utils/config';

const HeroSplit = ({ onROICalculatorClick, onShopClick, onContactClick }) => {
  const { t } = useTranslation();
  const { audience, isB2B, isB2C, openSelector } = useAudience();
  const { getTestConfig, trackConversion, trackImpression, isLoaded: abTestLoaded } = useABTest();
  const [partners, setPartners] = useState([]);
  const [activePanel, setActivePanel] = useState(null);
  const [hasTrackedImpression, setHasTrackedImpression] = useState(false);
  const [showAssessment, setShowAssessment] = useState(false);

  // Get A/B test configurations for each audience
  const b2bHeroConfig = getTestConfig('HERO_HEADLINE', 'b2b');
  const b2cHeroConfig = getTestConfig('HERO_HEADLINE', 'b2c');
  const b2bCtaConfig = getTestConfig('CTA_BUTTON', 'b2b');
  const b2cCtaConfig = getTestConfig('CTA_BUTTON', 'b2c');

  // Track impression on first render
  useEffect(() => {
    if (abTestLoaded && !hasTrackedImpression) {
      trackImpression('hero_headline');
      trackImpression('cta_button');
      setHasTrackedImpression(true);
    }
  }, [abTestLoaded, hasTrackedImpression, trackImpression]);

  useEffect(() => {
    const fetchPartners = async () => {
      try {
        const response = await fetch(apiUrl("partners"));
        if (response.ok) {
          const data = await response.json();
          setPartners(data || []);
        }
      } catch {
        setPartners([]);
      }
    };
    fetchPartners();
  }, []);

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleShopClick = useCallback((type) => {
    if (onShopClick) {
      onShopClick(type);
    }
  }, [onShopClick]);

  // Track CTA click conversions
  const handlePrimaryCTAClick = useCallback((panelKey) => {
    const ctaConfig = panelKey === 'b2b' ? b2bCtaConfig : b2cCtaConfig;
    const heroConfig = panelKey === 'b2b' ? b2bHeroConfig : b2cHeroConfig;
    
    trackConversion('cta_button', 'primary_click', { panel: panelKey, variant: ctaConfig?.variant });
    trackConversion('hero_headline', 'cta_click', { panel: panelKey, variant: heroConfig?.variant });
    
    if (panelKey === 'b2b') {
      openBookDemo();
    } else {
      // For B2C, open the 5D Assessment
      setShowAssessment(true);
    }
  }, [trackConversion, b2bCtaConfig, b2cCtaConfig, b2bHeroConfig, b2cHeroConfig]);

  const handleSecondaryCTAClick = useCallback((panelKey) => {
    const ctaConfig = panelKey === 'b2b' ? b2bCtaConfig : b2cCtaConfig;
    
    trackConversion('cta_button', 'secondary_click', { panel: panelKey, variant: ctaConfig?.variant });
    
    // Both B2B and B2C now open ROI calculator
    onROICalculatorClick?.();
  }, [trackConversion, b2bCtaConfig, b2cCtaConfig, onROICalculatorClick]);

  // Get CTA button color based on A/B test variant
  const getButtonGradient = (defaultGradient, ctaColor) => {
    const colorMap = {
      'blue': 'from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700',
      'indigo': 'from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700',
      'purple': 'from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700',
      'emerald': 'from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700',
      'teal': 'from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700',
      'cyan': 'from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700',
    };
    return ctaColor ? colorMap[ctaColor] || defaultGradient : defaultGradient;
  };

  // Panel configurations with A/B test overrides
  // A/B tests now use translation keys, not hardcoded strings
  const panels = {
    b2b: {
      type: AUDIENCE_TYPES.B2B,
      gradient: 'from-blue-900 via-blue-800 to-indigo-900',
      bgImage: '/images/corporate-hero.png',
      accentColor: 'text-blue-400',
      buttonGradient: getButtonGradient('from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700', b2bCtaConfig?.color),
      icon: Building2,
      badge: t('hero.b2b.badge', 'For Organizations'),
      // Use A/B test variant translation key if available, otherwise use default
      title: b2bHeroConfig?.titleKey ? t(b2bHeroConfig.titleKey, t('hero.b2b.title')) : t('hero.b2b.title', 'Turn Leadership Effort into Measurable Business Impact'),
      subtitle: b2bHeroConfig?.subtitleKey ? t(b2bHeroConfig.subtitleKey, t('hero.b2b.subtitle')) : t('hero.b2b.subtitle'),
      positioning: t('hero.b2b.positioning', ''),
      stats: [
        { value: '65%', label: t('hero.b2b.stat1', 'Higher Profitability') },
        { value: '3.5x', label: t('hero.b2b.stat2', 'ROI on Investment') },
        { value: '62%', label: t('hero.b2b.stat3', 'Better Retention') },
      ],
      cta: b2bCtaConfig?.textKey ? t(b2bCtaConfig.textKey, t('hero.b2b.cta')) : t('hero.b2b.cta', 'Book a Demo'),
      secondaryCta: t('hero.b2b.secondaryCta', 'Calculate ROI'),
      features: [
        { icon: Users, text: t('hero.b2b.feature1', 'Team Development Programs') },
        { icon: BarChart3, text: t('hero.b2b.feature2', 'Real-time ROI Analytics') },
        { icon: Award, text: t('hero.b2b.feature3', 'Certified Expert Coaches') },
      ]
    },
    b2c: {
      type: AUDIENCE_TYPES.B2C,
      gradient: 'from-emerald-900 via-teal-800 to-cyan-900',
      bgImage: '/images/individual-hero.png',
      accentColor: 'text-emerald-400',
      buttonGradient: getButtonGradient('from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700', b2cCtaConfig?.color),
      icon: User,
      badge: t('hero.b2c.badge', 'For Individuals'),
      // Use A/B test variant translation key if available, otherwise use default
      title: b2cHeroConfig?.titleKey ? t(b2cHeroConfig.titleKey, t('hero.b2c.title')) : t('hero.b2c.title'),
      subtitle: b2cHeroConfig?.subtitleKey ? t(b2cHeroConfig.subtitleKey, t('hero.b2c.subtitle')) : t('hero.b2c.subtitle'),
      positioning: t('hero.b2c.positioning', ''),
      stats: [
        { value: '85%', label: t('hero.b2c.stat1', 'Goal Achievement') },
        { value: '4.9', label: t('hero.b2c.stat2', 'Coach Rating') },
        { value: '10K+', label: t('hero.b2c.stat3', 'Sessions Delivered') },
      ],
      cta: b2cCtaConfig?.textKey ? t(b2cCtaConfig.textKey, t('hero.b2c.cta')) : t('hero.b2c.cta', 'Find Your Coach'),
      secondaryCta: t('hero.b2c.secondaryCta', 'Calculate ROI'),
      features: [
        { icon: Target, text: t('hero.b2c.feature1', 'Goal-Focused Sessions') },
        { icon: Calendar, text: t('hero.b2c.feature2', 'Flexible Scheduling') },
        { icon: TrendingUp, text: t('hero.b2c.feature3', 'Track Your Progress') },
      ]
    }
  };

  // Render a single panel
  const renderPanel = (panelKey, isFullWidth = false) => {
    const panel = panels[panelKey];
    const Icon = panel.icon;
    const isHovered = activePanel === panelKey;
    
    return (
      <div 
        className={`
          relative overflow-hidden transition-all duration-500
          ${isFullWidth ? 'w-full' : 'w-1/2'}
          ${!isFullWidth && isHovered ? 'w-[55%]' : ''}
          ${!isFullWidth && activePanel && !isHovered ? 'w-[45%]' : ''}
        `}
        onMouseEnter={() => !isFullWidth && setActivePanel(panelKey)}
        onMouseLeave={() => !isFullWidth && setActivePanel(null)}
      >
        <div className={`
          relative h-full
          px-8 lg:px-12 py-16 lg:py-20
          flex flex-col justify-center items-center
        `}>
          {/* Background Image */}
          <div 
            className="absolute inset-0 bg-cover bg-top bg-no-repeat"
            style={{ backgroundImage: `url(${panel.bgImage})` }}
          />
          {/* Gradient Overlay */}
          <div className={`absolute inset-0 bg-gradient-to-r ${panel.gradient} opacity-70`} />

          {/* Content Container */}
          <div className="relative z-10 max-w-3xl text-center">
            {/* Text Content */}
            <div>
              {/* Badge */}
              <div className={`inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium text-white mb-6`}>
                <Icon className="w-4 h-4" />
                {panel.badge}
              </div>

              {/* Title */}
              <h1 className="text-3xl lg:text-4xl xl:text-5xl font-bold text-white mb-4 leading-tight">
                {panel.title}
              </h1>

              {/* Subtitle */}
              <p className="text-lg text-white/80 mb-4 leading-relaxed max-w-2xl mx-auto">
                {panel.subtitle}
              </p>

              {/* Positioning Line */}
              {panel.positioning && (
                <p className="text-base font-semibold text-white/90 mb-8 italic">
                  {panel.positioning}
                </p>
              )}

              {/* Features */}
              <div className="flex flex-wrap justify-center gap-4 mb-8">
                {panel.features.map((feature, idx) => {
                  const FeatureIcon = feature.icon;
                  return (
                    <div key={feature.text} className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center">
                        <FeatureIcon className={`w-4 h-4 ${panel.accentColor}`} />
                      </div>
                      <span className="text-white/90 text-sm font-medium">{feature.text}</span>
                    </div>
                  );
                })}
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 mb-8 py-6 border-y border-white/10">
                {panel.stats.map((stat, idx) => (
                  <div key={stat.label} className="text-center">
                    <div className={`text-2xl lg:text-3xl font-bold ${panel.accentColor}`}>{stat.value}</div>
                    <div className="text-xs text-white/60 mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>

              {/* CTAs */}
              <div className="flex flex-wrap justify-center gap-4">
                <Button
                  size="lg"
                  className={`bg-gradient-to-r ${panel.buttonGradient} text-white px-6 py-6 text-base font-semibold shadow-lg hover:shadow-xl transition-all rounded-xl`}
                  onClick={() => handlePrimaryCTAClick(panelKey)}
                  data-testid={`hero-${panelKey}-primary-cta`}
                >
                  {panel.cta}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-2 border-white/30 text-white hover:bg-white/10 px-6 py-6 text-base font-semibold rounded-xl"
                  onClick={() => handleSecondaryCTAClick(panelKey)}
                  data-testid={`hero-${panelKey}-secondary-cta`}
                >
                  <Calculator className="mr-2 h-5 w-5" />
                  {panel.secondaryCta}
                </Button>
                {/* View Packages button for B2C only */}
                {panelKey === 'b2c' && (
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-2 border-white/30 text-white hover:bg-white/10 px-6 py-6 text-base font-semibold rounded-xl"
                    onClick={() => handleShopClick('individual')}
                    data-testid="hero-b2c-packages-cta"
                  >
                    <Package className="mr-2 h-5 w-5" />
                    {t('hero.b2c.viewPackages', 'View Packages')}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <section className="relative min-h-screen pt-[7.5rem]">
      {/* Desktop: Split View or Single Panel based on audience */}
      <div className="hidden lg:flex min-h-[calc(100vh-7.5rem)]">
        {(!audience || audience === AUDIENCE_TYPES.NONE) ? (
          // No selection: Show both panels side by side
          <>
            {renderPanel('b2b')}
            {renderPanel('b2c')}
          </>
        ) : isB2B ? (
          // B2B selected: Show B2B full width
          renderPanel('b2b', true)
        ) : (
          // B2C selected: Show B2C full width
          renderPanel('b2c', true)
        )}
      </div>

      {/* Mobile: Stacked Panels or Single Panel */}
      <div className="lg:hidden">
        {(!audience || audience === AUDIENCE_TYPES.NONE) ? (
          // No selection: Show both stacked
          <div className="flex flex-col">
            <div className="relative px-6 py-12">
              <div className="absolute inset-0 bg-cover bg-top" style={{ backgroundImage: `url(${panels.b2b.bgImage})` }} />
              <div className={`absolute inset-0 bg-gradient-to-r ${panels.b2b.gradient} opacity-70`} />
              <div className="relative z-10 text-center">
                <MobilePanel panel={panels.b2b} panelKey="b2b" onPrimaryCta={scrollToContact} onSecondaryCta={onROICalculatorClick} />
              </div>
            </div>
            <div className="relative px-6 py-12">
              <div className="absolute inset-0 bg-cover bg-top" style={{ backgroundImage: `url(${panels.b2c.bgImage})` }} />
              <div className={`absolute inset-0 bg-gradient-to-r ${panels.b2c.gradient} opacity-70`} />
              <div className="relative z-10 text-center">
                <MobilePanel panel={panels.b2c} panelKey="b2c" onPrimaryCta={() => handleShopClick('individual')} onSecondaryCta={() => handleShopClick('individual')} />
              </div>
            </div>
          </div>
        ) : isB2B ? (
          <div className="relative px-6 py-16 min-h-[calc(100vh-7.5rem)]">
            <div className="absolute inset-0 bg-cover bg-top" style={{ backgroundImage: `url(${panels.b2b.bgImage})` }} />
            <div className={`absolute inset-0 bg-gradient-to-r ${panels.b2b.gradient} opacity-70`} />
            <div className="relative z-10 text-center">
              <MobilePanel panel={panels.b2b} panelKey="b2b" onPrimaryCta={scrollToContact} onSecondaryCta={onROICalculatorClick} />
            </div>
          </div>
        ) : (
          <div className="relative px-6 py-16 min-h-[calc(100vh-7.5rem)]">
            <div className="absolute inset-0 bg-cover bg-top" style={{ backgroundImage: `url(${panels.b2c.bgImage})` }} />
            <div className={`absolute inset-0 bg-gradient-to-r ${panels.b2c.gradient} opacity-70`} />
            <div className="relative z-10 text-center">
              <MobilePanel panel={panels.b2c} panelKey="b2c" onPrimaryCta={() => setShowAssessment(true)} onSecondaryCta={() => handleShopClick('individual')} />
            </div>
          </div>
        )}
      </div>

      {/* Switch Audience Button (shows when audience is selected) */}
      {audience && audience !== AUDIENCE_TYPES.NONE && (
        <button
          onClick={openSelector}
          className="fixed bottom-6 right-6 z-50 bg-white shadow-lg rounded-full px-4 py-2 flex items-center gap-2 text-sm font-medium text-gray-700 hover:shadow-xl transition-all"
          data-testid="switch-audience-btn"
        >
          <Sparkles className="w-4 h-4 text-blue-600" />
          {t('audience.switch', 'Switch View')}
        </button>
      )}

      {/* Partners Banner */}
      {partners.length > 0 && (
        <div className="bg-white border-t border-gray-100 py-6 overflow-hidden">
          <div className="flex items-center justify-center gap-2 mb-4">
            <p className="text-sm text-gray-500 font-medium">{t('hero.trustedBy')}</p>
          </div>
          <div className="relative">
            <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-white to-transparent z-10" />
            <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-white to-transparent z-10" />
            <div className="flex animate-scroll">
              {[...partners, ...partners].map((partner, index) => (
                <div key={`${partner.name}-${index}`} className="flex-shrink-0 mx-8 flex items-center justify-center h-12">
                  {partner.logo ? (
                    <img 
                      src={partner.logo} 
                      alt={partner.name}
                      className="h-8 w-auto max-w-[120px] object-contain grayscale hover:grayscale-0 transition-all opacity-60 hover:opacity-100"
                    />
                  ) : (
                    <span className="text-xl font-bold text-gray-400">{partner.name}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-scroll {
          animation: scroll 30s linear infinite;
        }
        .animate-scroll:hover {
          animation-play-state: paused;
        }
      `}</style>

      {/* 5D Assessment Modal */}
      <FiveDAssessment 
        isOpen={showAssessment}
        onClose={() => setShowAssessment(false)}
        onComplete={(results) => {
          setShowAssessment(false);
          handleShopClick('individual');
        }}
      />
    </section>
  );
};

// Mobile Panel Component
const MobilePanel = ({ panel, panelKey, onPrimaryCta, onSecondaryCta }) => {
  const Icon = panel.icon;
  
  return (
    <div className="max-w-xl mx-auto">
      <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium text-white mb-6">
        <Icon className="w-4 h-4" />
        {panel.badge}
      </div>
      
      <h2 className="text-3xl font-bold text-white mb-4">{panel.title}</h2>
      <p className="text-white/80 mb-6">{panel.subtitle}</p>
      
      <div className="grid grid-cols-3 gap-3 mb-6 py-4 border-y border-white/10">
        {panel.stats.map((stat, idx) => (
          <div key={stat.label} className="text-center">
            <div className={`text-xl font-bold ${panel.accentColor}`}>{stat.value}</div>
            <div className="text-xs text-white/60">{stat.label}</div>
          </div>
        ))}
      </div>
      
      <div className="flex flex-col gap-3">
        <Button
          className={`w-full bg-gradient-to-r ${panel.buttonGradient} text-white py-6 font-semibold rounded-xl`}
          onClick={onPrimaryCta}
          data-testid={`hero-${panelKey}-mobile-primary-cta`}
        >
          {panel.cta}
          <ArrowRight className="ml-2 h-5 w-5" />
        </Button>
        <Button
          variant="outline"
          className="w-full border-2 border-white/30 text-white hover:bg-white/10 py-6 font-semibold rounded-xl"
          onClick={onSecondaryCta}
          data-testid={`hero-${panelKey}-mobile-secondary-cta`}
        >
          {panel.secondaryCta}
        </Button>
      </div>
    </div>
  );
};

export default HeroSplit;
